import React from "react";
import { Button, Flex, Space, Tooltip, Typography } from "@payping/web-components";
import { Add } from "@payping/icons";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { IBANItem, IBANStatus } from "../types";

import ShebaCard from "./ShebaCard";

interface ApprovedIBANsSectionProps {
  approvedIBANs: IBANItem[];
  onSettlementSelection: (ibanId: string) => void;
  onAddNewIBAN: () => void;
  onRemoveIBAN: (ibanId: string) => void;
  getStatusInfo: (status: IBANStatus) => { color: string; text: string; icon: React.ReactNode };
  selectedIBANId?: string | null;
  hasPendingRequests?: boolean;
  activeIBAN?: string | null;
  hasPendingIBANs?: boolean;
}

const EmptyStateIcon: React.FC = () => (
  <svg
    width="122"
    height="100"
    viewBox="0 0 122 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="tw-mb-4"
  >
    <path
      d="M60.6033 99.4926C85.237 99.4926 105.207 95.7613 105.207 91.1584C105.207 86.5556 85.237 82.8242 60.6033 82.8242C35.9696 82.8242 16 86.5556 16 91.1584C16 95.7613 35.9696 99.4926 60.6033 99.4926Z"
      fill="#F5F5F7"
      fillOpacity="0.8"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M96.2863 66.6734L80.5462 47.3017C79.791 46.3899 78.687 45.8379 77.5245 45.8379H43.6824C42.5206 45.8379 41.4166 46.3899 40.6613 47.3017L24.9219 66.6734V76.7938H96.287V66.6734H96.2863Z"
      fill="#AEB8C2"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M38.2566 20.8359H82.9493C83.6473 20.8359 84.3166 21.1132 84.8102 21.6067C85.3037 22.1002 85.5809 22.7696 85.5809 23.4675V84.878C85.5809 85.576 85.3037 86.2453 84.8102 86.7388C84.3166 87.2324 83.6473 87.5096 82.9493 87.5096H38.2566C37.5586 87.5096 36.8893 87.2324 36.3958 86.7388C35.9023 86.2453 35.625 85.576 35.625 84.878V23.4675C35.625 22.7696 35.9023 22.1002 36.3958 21.6067C36.8893 21.1132 37.5586 20.8359 38.2566 20.8359V20.8359Z"
      fill="#F5F5F7"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M44.0772 27.3848H77.1278C77.4768 27.3848 77.8115 27.5234 78.0582 27.7702C78.305 28.0169 78.4436 28.3516 78.4436 28.7006V45.1196C78.4436 45.4686 78.305 45.8033 78.0582 46.05C77.8115 46.2968 77.4768 46.4354 77.1278 46.4354H44.0772C43.7282 46.4354 43.3935 46.2968 43.1468 46.05C42.9 45.8033 42.7614 45.4686 42.7614 45.1196V28.7006C42.7614 28.3516 42.9 28.0169 43.1468 27.7702C43.3935 27.5234 43.7282 27.3848 44.0772 27.3848ZM44.2495 53.5782H76.9555C77.3501 53.5782 77.7287 53.735 78.0077 54.0141C78.2868 54.2931 78.4436 54.6717 78.4436 55.0663C78.4436 55.461 78.2868 55.8395 78.0077 56.1186C77.7287 56.3977 77.3501 56.5545 76.9555 56.5545H44.2495C43.8548 56.5545 43.4763 56.3977 43.1972 56.1186C42.9182 55.8395 42.7614 55.461 42.7614 55.0663C42.7614 54.6717 42.9182 54.2931 43.1972 54.0141C43.4763 53.735 43.8548 53.5782 44.2495 53.5782ZM44.2495 61.317H76.9555C77.3502 61.317 77.7288 61.4738 78.008 61.753C78.2871 62.0321 78.4439 62.4107 78.4439 62.8055C78.4439 63.2003 78.2871 63.5789 78.008 63.858C77.7288 64.1372 77.3502 64.294 76.9555 64.294H44.2495C43.8548 64.294 43.4762 64.1372 43.197 63.858C42.9179 63.5789 42.761 63.2003 42.761 62.8055C42.761 62.4107 42.9179 62.0321 43.197 61.753C43.4762 61.4738 43.8548 61.317 44.2495 61.317ZM96.1397 89.9367C95.6298 91.9571 93.839 93.4631 91.7087 93.4631H29.4962C27.366 93.4631 25.5752 91.9565 25.066 89.9367C24.9689 89.5517 24.9198 89.1562 24.9199 88.7591V66.6756H42.2344C44.1469 66.6756 45.6883 68.2861 45.6883 70.2413V70.2677C45.6883 72.2223 47.2476 73.8006 49.1601 73.8006H72.0449C73.9574 73.8006 75.5166 72.2078 75.5166 70.2525V70.2446C75.5166 68.2894 77.0581 66.6749 78.9706 66.6749H96.2851V88.7598C96.2851 89.1657 96.2344 89.5598 96.1397 89.9367Z"
      fill="#DCE0E6"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M98.3162 21.9026L93.8227 23.6461C93.7073 23.6909 93.5815 23.7023 93.4599 23.6789C93.3383 23.6555 93.2258 23.5982 93.1352 23.5137C93.0447 23.4292 92.9799 23.3208 92.9483 23.2011C92.9166 23.0813 92.9194 22.9551 92.9563 22.8368L94.2306 18.7533C92.5273 16.8164 91.5273 14.4546 91.5273 11.9059C91.5273 5.33026 98.1839 0 106.396 0C114.606 0 121.263 5.33026 121.263 11.9059C121.263 18.4816 114.606 23.8118 106.395 23.8118C103.416 23.8118 100.642 23.1105 98.3162 21.9026Z"
      fill="#DCE0E6"
    />
    <path
      d="M112.251 14.0555C113.286 14.0555 114.126 13.2264 114.126 12.2035C114.126 11.1807 113.286 10.3516 112.251 10.3516C111.216 10.3516 110.377 11.1807 110.377 12.2035C110.377 13.2264 111.216 14.0555 112.251 14.0555Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M102.413 13.8248H98.6641L100.571 10.584L102.413 13.8248ZM104.756 10.584H108.035V13.8248H104.756V10.584Z"
      fill="white"
    />
  </svg>
);

interface ApprovedSectionHeaderProps {
  onAddNewIBAN: () => void;
  hasPendingRequests: boolean;
}

const ApprovedSectionHeader: React.FC<ApprovedSectionHeaderProps> = ({ onAddNewIBAN, hasPendingRequests }) => {
  const { t } = useAppTranslation();

  return (
    <Flex justify="space-between" align="center" className="tw-mb-4 tw-mt-5 tw-w-full">
      <Typography.Text className="tw-text-base">{t("profile.bank.approvedAccounts")}</Typography.Text>
      <Tooltip title={hasPendingRequests ? t("profile.bank.pendingRequestTooltip") : ""}>
        <Button
          type="default"
          icon={<Add />}
          onClick={onAddNewIBAN}
          className="tw-w-fit"
          disabled={hasPendingRequests}
        >
          {t("profile.bank.addNewSheba")}
        </Button>
      </Tooltip>
    </Flex>
  );
};

const EmptyApprovedIBANsState: React.FC = () => {
  const { t } = useAppTranslation();

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8">
      <EmptyStateIcon />
      <Typography.Text className="tw-text-gray-600">{t("profile.bank.noApprovedAccount")}</Typography.Text>
    </div>
  );
};

interface ApprovedIBANListProps {
  approvedIBANs: IBANItem[];
  onSettlementSelection: (ibanId: string) => void;
  onRemoveIBAN: (ibanId: string) => void;
  getStatusInfo: (status: IBANStatus) => { color: string; text: string; icon: React.ReactNode };
  selectedIBANId?: string | null;
  hasPendingRequests: boolean;
  activeIBAN?: string | null;
}

const ApprovedIBANList: React.FC<ApprovedIBANListProps> = ({
  approvedIBANs,
  onSettlementSelection,
  onRemoveIBAN,
  getStatusInfo,
  selectedIBANId,
  hasPendingRequests,
  activeIBAN,
}) => {
  return (
    <Space direction="vertical" className="tw-w-full" size="middle">
      {approvedIBANs.map((iban) => {
        const statusInfo = getStatusInfo(iban.status);
        const isChecked = selectedIBANId === iban.id;
        const isActiveIBAN = activeIBAN === iban.iban;
        return (
          <ShebaCard
            onClickRadio={() => onSettlementSelection(iban.id)}
            checked={isChecked}
            disabled={
              iban.status !== IBANStatus.SETTLEMENT_READY && iban.status !== IBANStatus.APPROVED && !isChecked
            }
            onDelete={() => onRemoveIBAN(iban.id)}
            key={iban.id}
            sheba={iban.iban}
            title={iban.title}
            status={iban.status === IBANStatus.REJECTED ? "rejected" : "active"}
            statusText={statusInfo.text}
            showSettlementWarning={true}
            disableDelete={hasPendingRequests || isActiveIBAN}
            isActiveIBAN={isActiveIBAN}
          />
        );
      })}
    </Space>
  );
};

export const ApprovedIBANsSection: React.FC<ApprovedIBANsSectionProps> = ({
  approvedIBANs,
  onSettlementSelection,
  onAddNewIBAN,
  onRemoveIBAN,
  getStatusInfo,
  selectedIBANId,
  hasPendingRequests = false,
  activeIBAN,
  hasPendingIBANs = false,
}) => {
  // Show empty state with hourglass icon when no approved IBANs but there are pending ones
  if (approvedIBANs.length === 0 && hasPendingIBANs) {
    return (
      <div>
        <ApprovedSectionHeader onAddNewIBAN={onAddNewIBAN} hasPendingRequests={hasPendingRequests} />
        <EmptyApprovedIBANsState />
      </div>
    );
  }

  if (approvedIBANs.length === 0) return null;

  return (
    <div>
      <ApprovedSectionHeader onAddNewIBAN={onAddNewIBAN} hasPendingRequests={hasPendingRequests} />
      <ApprovedIBANList
        approvedIBANs={approvedIBANs}
        onSettlementSelection={onSettlementSelection}
        onRemoveIBAN={onRemoveIBAN}
        getStatusInfo={getStatusInfo}
        selectedIBANId={selectedIBANId}
        hasPendingRequests={hasPendingRequests}
        activeIBAN={activeIBAN}
      />
    </div>
  );
};
